import { ChartKind } from '../../components/platinum-table-old/lib/chart-guide'

export type DataType = 'freq' | 'colPct' | 'rowPct'

// Map chart types from structure.json to our ChartKind types
const chartTypeMapping: Record<string, ChartKind> = {
  'h_bar_chart': 'bar',
  'h_clustered_bar_chart': 'clusteredBar',
  'h_stacked_bar_chart': 'stackedBar',
}

// Convert column letters to indices (A=0, B=1, C=2, etc.)
const letterToIndex = (letter: string): number => {
  if (!letter || letter === '') return -1
  return letter.charCodeAt(0) - 65 // A=0, B=1, C=2, etc.
}

export interface RealVisualizationSlide {
  id: string
  title: string
  description: string
  tableFile: string
  tableName: string
  chartType: ChartKind
  dataType: DataType
  columnLetters: string[] // Direct from structure.json
  useAllColumns?: boolean
}

// Directly based on structure.json
// Note: Column letters map to actual array indices (including spacers)
// A=0 (Total), B=1 (Spacer), C=2 (Male), D=3 (Female), etc.
export const realVisualizationSlidesV2: RealVisualizationSlide[] = [
  {
    id: 'slide-1',
    title: 'Women\'s Football Themes Overview',
    description: 'Main themes from responses about women\'s football',
    tableFile: 'InGeneral_Themes.json',
    tableName: 'InGeneral_Themes',
    chartType: 'bar',
    dataType: 'colPct',
    columnLetters: ['0'], // Use first column (Total)
    useAllColumns: false
  },
  {
    id: 'slide-2',
    title: 'Themes by Gender',
    description: 'How themes vary between male and female respondents',
    tableFile: 'InGeneral_Themes_Analysis.json',
    tableName: 'InGeneral_Themes_Analysis',
    chartType: 'clusteredBar',
    dataType: 'colPct',
    columnLetters: ['2', '3'], // Indices for Male, Female (accounting for spacers)
    useAllColumns: false
  },
  {
    id: 'slide-3',
    title: 'Overall Sentiment Analysis',
    description: 'Sentiment breakdown of responses about women\'s football',
    tableFile: 'InGeneral_Sentiment_Analysis.json',
    tableName: 'InGeneral_Sentiment_Analysis',
    chartType: 'stackedBar', // Custom diverging bar for sentiment
    dataType: 'colPct', // Column percentages to show distribution
    columnLetters: ['0'], // Just Total column
    useAllColumns: false
  },
  {
    id: 'slide-4',
    title: 'Themes with Sentiment',
    description: 'Sentiment breakdown across different themes',
    tableFile: 'InGeneral_Themes_Sentiment.json',
    tableName: 'InGeneral_Themes_Sentiment',
    chartType: 'stackedBar',
    dataType: 'colPct',
    columnLetters: ['ALL'],
    useAllColumns: true
  },
  {
    id: 'slide-5',
    title: 'Emotional Response - Comparison to Men\'s Football',
    description: 'Emotional response for comparison theme',
    tableFile: 'InGeneral_Themes_Emotion.json',
    tableName: 'InGeneral_Themes_Emotion',
    chartType: 'column',
    dataType: 'colPct',
    columnLetters: ['A'], // Column A: "Comparison to Men's Football"
    useAllColumns: false
  },
  {
    id: 'slide-6',
    title: 'Emotional Response - Skill and Quality',
    description: 'Emotional response for skill and quality theme',
    tableFile: 'InGeneral_Themes_Emotion.json',
    tableName: 'InGeneral_Themes_Emotion',
    chartType: 'column',
    dataType: 'colPct',
    columnLetters: ['B'], // Column B: "Skill and Quality of Play"
    useAllColumns: false
  },
  {
    id: 'slide-7',
    title: 'Emotional Response - Role Models',
    description: 'Emotional response for role models theme',
    tableFile: 'InGeneral_Themes_Emotion.json',
    tableName: 'InGeneral_Themes_Emotion',
    chartType: 'column',
    dataType: 'colPct',
    columnLetters: ['D'], // Column D: "Role Models and Inspiration for Girls"
    useAllColumns: false
  },
  {
    id: 'slide-8',
    title: 'Emotional Response - Marketing Needs',
    description: 'Emotional response for marketing and promotion theme',
    tableFile: 'InGeneral_Themes_Emotion.json',
    tableName: 'InGeneral_Themes_Emotion',
    chartType: 'column',
    dataType: 'colPct',
    columnLetters: ['F'], // Column F: "Need for More Marketing and Promotion"
    useAllColumns: false
  }
]

// Helper to convert column letters to indices for a specific table
export const getColumnIndicesForSlide = (slide: RealVisualizationSlide, tableData: any): number[] => {
  if (slide.useAllColumns || slide.columnLetters.includes('ALL')) {
    return [] // Return empty to use all columns
  }
  
  const topVecs = tableData?.top?.vecs || tableData?.Top?.Vecs || []
  const indices: number[] = []
  
  slide.columnLetters.forEach(col => {
    // Check if it's a numeric index (new format) or a letter (old format)
    const index = isNaN(Number(col)) ? letterToIndex(col) : Number(col)
    if (index >= 0 && index < topVecs.length) {
      indices.push(index)
    }
  })
  
  return indices
}

// Enhanced filter function that preserves data integrity
export const filterColumnsForSlideV2 = (tableData: any, slide: RealVisualizationSlide): any => {
  // Normalize the data structure to use capital field names expected by SmartChart
  const normalizedData = normalizeTableData(tableData)
  
  if (!normalizedData) {
    return normalizedData
  }
  
  // Always filter out Base rows from the data
  const filteredData = filterBaseRows(normalizedData)
  
  // For ALL columns or no specific columns, return data with Base rows filtered
  if (slide.useAllColumns || slide.columnLetters.includes('ALL')) {
    return filteredData
  }

  // Get the column indices we want (accounting for spacer columns)
  const indices = getColumnIndicesForSlide(slide, filteredData)
  
  // If no valid indices, return data with Base rows filtered
  if (indices.length === 0) {
    return filteredData
  }

  // For simple bar and column charts with single column selection, return full data (with Base rows filtered)
  // The chart settings will specify which column to use
  if ((slide.chartType === 'bar' || slide.chartType === 'column') && indices.length === 1) {
    return filteredData
  }

  // Clone the data for column filtering
  const columnFilteredData = JSON.parse(JSON.stringify(filteredData))
  
  // Get the vecs and cells
  const topVecs = columnFilteredData.Top?.Vecs || []
  const cellRows = columnFilteredData.Cells?.Rows || []

  // Filter columns (excluding spacers from indices)
  const filteredVecs = indices.map(idx => topVecs[idx]).filter(vec => 
    vec && vec.Type !== 'Spacer' && vec.type !== 'Spacer'
  )
  
  // If no valid columns after filtering, return data with Base rows filtered
  if (filteredVecs.length === 0) {
    return filteredData
  }
  
  // Filter cell data to match columns (Base rows already filtered)
  const filteredRows = cellRows.map((row: any) => {
    const cells = row.Cell || []
    const filteredCells = indices.map(idx => cells[idx]).filter(Boolean)
    
    return {
      ...row,
      Cell: filteredCells
    }
  })

  // Update the filtered data with column filtering
  columnFilteredData.Top.Vecs = filteredVecs
  columnFilteredData.Cells.Rows = filteredRows

  return columnFilteredData
}

// Helper function to filter out Base and Arith rows from the data
const filterBaseRows = (tableData: any): any => {
  if (!tableData) return tableData
  
  // Clone the data
  const filtered = JSON.parse(JSON.stringify(tableData))
  
  // Get side vectors and find non-Base/non-Arith indices
  const sideVecs = filtered.Side?.Vecs || []
  const validIndices: number[] = []
  
  const filteredSideVecs = sideVecs.filter((vec: any, idx: number) => {
    const isBase = vec && (vec.Type === 'Base' || vec.type === 'Base')
    const isArith = vec && (vec.Type === 'Arith' || vec.type === 'Arith')
    const label = vec?.Label?.toLowerCase() || vec?.label?.toLowerCase() || ''
    const isSentimentIndex = label.includes('sentiment index')
    
    // Keep sentiment index even if it's Arith type
    if (isSentimentIndex) {
      validIndices.push(idx)
      return true
    }
    
    if (!isBase && !isArith) {
      validIndices.push(idx)
      return true
    }
    return false
  })
  
  // Filter cell rows to exclude Base and Arith rows
  const cellRows = filtered.Cells?.Rows || []
  const filteredCellRows = cellRows.filter((row: any, idx: number) => 
    validIndices.includes(idx)
  )
  
  // Update the filtered data
  if (filtered.Side) {
    filtered.Side.Vecs = filteredSideVecs
  }
  if (filtered.Cells) {
    filtered.Cells.Rows = filteredCellRows
  }
  
  return filtered
}

// Helper function to normalize data structure to use capital field names
const normalizeTableData = (tableData: any): any => {
  if (!tableData) return null
  
  // Clone the data
  const normalized = JSON.parse(JSON.stringify(tableData))
  
  // Normalize field names to capitals (expected by SmartChart)
  if (normalized.top && !normalized.Top) {
    normalized.Top = normalized.top
    delete normalized.top
  }
  if (normalized.side && !normalized.Side) {
    normalized.Side = normalized.side
    delete normalized.side
  }
  if (normalized.cells && !normalized.Cells) {
    normalized.Cells = normalized.cells
    delete normalized.cells
  }
  
  // Normalize nested fields
  if (normalized.Top) {
    if (normalized.Top.vecs && !normalized.Top.Vecs) {
      normalized.Top.Vecs = normalized.Top.vecs
      delete normalized.Top.vecs
    }
    if (normalized.Top.groups && !normalized.Top.Groups) {
      normalized.Top.Groups = normalized.Top.groups
      delete normalized.Top.groups
    }
  }
  
  if (normalized.Side) {
    if (normalized.Side.vecs && !normalized.Side.Vecs) {
      normalized.Side.Vecs = normalized.Side.vecs
      delete normalized.Side.vecs
    }
  }
  
  if (normalized.Cells) {
    if (normalized.Cells.rows && !normalized.Cells.Rows) {
      normalized.Cells.Rows = normalized.Cells.rows
      delete normalized.Cells.rows
    }
    
    // Normalize cell structure
    if (normalized.Cells.Rows) {
      normalized.Cells.Rows = normalized.Cells.Rows.map((row: any) => {
        const normalizedRow = {...row}
        if (row.cell && !row.Cell) {
          normalizedRow.Cell = row.cell
          delete normalizedRow.cell
        }
        
        // Normalize cell properties
        if (normalizedRow.Cell) {
          normalizedRow.Cell = normalizedRow.Cell.map((cell: any) => {
            if (!cell) return cell
            const normalizedCell = {...cell}
            
            // Normalize field names to capitals
            if (cell.freq !== undefined && cell.Freq === undefined) {
              normalizedCell.Freq = cell.freq
              delete normalizedCell.freq
            }
            if (cell.colpc !== undefined && cell.Colpc === undefined) {
              normalizedCell.Colpc = cell.colpc
              delete normalizedCell.colpc
            }
            if (cell.rowpc !== undefined && cell.Rowpc === undefined) {
              normalizedCell.Rowpc = cell.rowpc
              delete normalizedCell.rowpc
            }
            if (cell.sig !== undefined && cell.Sig === undefined) {
              normalizedCell.Sig = cell.sig
              delete normalizedCell.sig
            }
            
            return normalizedCell
          })
        }
        
        return normalizedRow
      })
    }
  }
  
  // Normalize vec properties (label, type)
  const normalizeVec = (vec: any) => {
    if (!vec) return vec
    const normalized = {...vec}
    
    if (vec.label !== undefined && vec.Label === undefined) {
      normalized.Label = vec.label
      delete normalized.label
    }
    if (vec.type !== undefined && vec.Type === undefined) {
      normalized.Type = vec.type
      delete normalized.type
    }
    if (vec.letter !== undefined && vec.Letter === undefined) {
      normalized.Letter = vec.letter
      delete normalized.letter
    }
    
    return normalized
  }
  
  if (normalized.Top?.Vecs) {
    normalized.Top.Vecs = normalized.Top.Vecs.map(normalizeVec)
  }
  if (normalized.Side?.Vecs) {
    normalized.Side.Vecs = normalized.Side.Vecs.map(normalizeVec)
  }
  
  return normalized
}

// Get chart settings based on the slide configuration
export const getChartSettingsForSlide = (slide: RealVisualizationSlide, tableData: any): any => {
  // Normalize the data first
  const normalizedData = normalizeTableData(tableData)
  if (!normalizedData) return {}
  
  const settings: any = {}
  const topVecs = normalizedData.Top?.Vecs || []
  const sideVecs = normalizedData.Side?.Vecs || []
  
  // Get non-spacer columns with their indices
  const nonSpacerColumns: { label: string, index: number }[] = []
  topVecs.forEach((vec: any, idx: number) => {
    if (vec && vec.Type !== 'Spacer') {
      nonSpacerColumns.push({
        label: vec.Label || '',
        index: idx
      })
    }
  })
  
  // Get row labels (excluding Base and Arith rows)
  const rows = sideVecs
    .filter((vec: any) => vec && vec.Type !== 'Base' && vec.Type !== 'Arith')
    .map((vec: any) => vec.Label || '')
  
  // For clustered/stacked charts
  if (slide.chartType.includes('clustered') || slide.chartType.includes('stacked')) {
    // In chart generators:
    // - selectedCategories = Y-axis categories (rows)
    // - selectedMonths = Series/groups (columns)
    
    // Set the rows as selectedCategories (Y-axis)
    settings.selectedCategories = rows.slice(0, 15) // Limit for readability
    
    // Set the columns as selectedMonths (series/groups)
    if (!slide.useAllColumns && slide.columnLetters[0] !== 'ALL') {
      const selectedColumns: string[] = []
      
      slide.columnLetters.forEach(col => {
        // Check if it's a numeric index or a letter
        const targetIndex = isNaN(Number(col)) ? letterToIndex(col) : Number(col)
        // Find the column at this exact index if it's not a spacer
        const vec = topVecs[targetIndex]
        if (vec && vec.Type !== 'Spacer') {
          selectedColumns.push(vec.Label || '')
        }
      })
      
      settings.selectedMonths = selectedColumns.length > 0 ? selectedColumns : nonSpacerColumns.map(c => c.label)
    } else {
      settings.selectedMonths = nonSpacerColumns.map(c => c.label)
    }
  }
  
  // For simple bar charts
  if (slide.chartType === 'bar' && !slide.chartType.includes('clustered') && !slide.chartType.includes('stacked')) {
    // For bar charts, also set selectedCategories (the rows to display)
    settings.selectedCategories = rows.slice(0, 15) // Limit for readability
    
    // Select the specific column if specified
    if (!slide.useAllColumns && slide.columnLetters.length > 0 && slide.columnLetters[0] !== 'ALL') {
      const targetIndex = isNaN(Number(slide.columnLetters[0])) ? letterToIndex(slide.columnLetters[0]) : Number(slide.columnLetters[0])
      // Get the column at this exact index if it's not a spacer
      const vec = topVecs[targetIndex]
      if (vec && vec.Type !== 'Spacer') {
        settings.selectedHeader = vec.Label || 'Total'
      } else if (nonSpacerColumns.length > 0) {
        settings.selectedHeader = nonSpacerColumns[0].label
      }
    } else if (nonSpacerColumns.length > 0) {
      settings.selectedHeader = nonSpacerColumns[0].label
    }
  }
  
  // For column charts (vertical bars)
  if (slide.chartType === 'column' || slide.chartType === 'clusteredColumn') {
    // For column charts, set selectedCategories (the x-axis categories)
    settings.selectedCategories = rows.slice(0, 15) // Limit for readability
    
    // Select the specific column if specified
    if (!slide.useAllColumns && slide.columnLetters.length > 0 && slide.columnLetters[0] !== 'ALL') {
      const targetIndex = isNaN(Number(slide.columnLetters[0])) ? letterToIndex(slide.columnLetters[0]) : Number(slide.columnLetters[0])
      // Get the column at this exact index if it's not a spacer
      const vec = topVecs[targetIndex]
      if (vec && vec.Type !== 'Spacer') {
        settings.selectedHeader = vec.Label || 'Total'
      } else if (nonSpacerColumns.length > 0) {
        settings.selectedHeader = nonSpacerColumns[0].label
      }
    } else if (nonSpacerColumns.length > 0) {
      settings.selectedHeader = nonSpacerColumns[0].label
    }
    
    // Add dataType to settings for column charts
    settings.dataType = slide.dataType
  }
  
  return settings
}